import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { login } from "../../redux/auth";
import { clearMessage, setMessage } from "../../redux/message";
import { useTheme } from "@mui/material";
import {
  Alert,
  Box,
  Paper,
  Link,
  Avatar,
  Button,
  TextField,
  Typography,
} from "@mui/material";
import { LockOutlined } from "@mui/icons-material";

const Login = () => {
  const theme = useTheme();
  let navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [successful, setSuccessful] = useState(false);
  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.message);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(clearMessage());
  }, [dispatch]);

  const handleLogin = (event) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const username = data.get("username");
    const password = data.get("password");
    if (!username || !password) {
      setSuccessful(false);
      dispatch(setMessage("Both username and password are required"));
      return;
    }
    if (username.length < 3 || username.length > 20) {
      setSuccessful(false);
      dispatch(setMessage("Username must be between 3 and 20 characters long"));
      return;
    }
    if (password.length < 6 || password.length > 40) {
      setSuccessful(false);
      dispatch(setMessage("Password must be between 6 and 40 characters long"));
      return;
    }
    setLoading(true);
    dispatch(login({ username, password }))
      .unwrap()
      .then(() => {
        setSuccessful(true);
        navigate("/");
        window.location.reload();
      })
      .catch(() => {
        setSuccessful(false);
        setLoading(false);
      });
  };

  if (isLoggedIn) {
    return <Navigate to="/profile" />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100% - 3rem)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: "1.5rem", md: "2.5rem" },
      }}
    >
      <Paper
        sx={{
          width: "100%",
          maxWidth: "400px",
          p: { xs: "2rem", sm: "2.5rem" },
          backgroundColor: theme.palette.background.alt,
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <Avatar
            sx={{
              width: 52,
              height: 52,
              mb: "0.75rem",
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.primary[600],
            }}
          >
            <LockOutlined />
          </Avatar>
          <Typography variant="h3" fontWeight={700}>
            Welcome back
          </Typography>
          <Typography
            variant="body1"
            color={theme.palette.text.secondary}
            sx={{ mt: "0.35rem", mb: "1.5rem" }}
          >
            Sign in to continue
          </Typography>
        </Box>
        <Box component="form" onSubmit={handleLogin} noValidate width="100%">
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="secondary"
            size="large"
            sx={{ mt: "1.25rem", mb: "1rem" }}
            disabled={loading}
          >
            Sign In
          </Button>
          {message && (
            <Alert severity={successful ? "success" : "error"} sx={{ mb: "1rem" }}>
              {message}
            </Alert>
          )}
          <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap="0.5rem">
            <Link href="#" variant="body2">
              Forgot password?
            </Link>
            <Link href="/register" variant="body2">
              {"Don't have an account? Sign up"}
            </Link>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
